import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import { Icons, Layout, MUI, Typography } from '@styles';

const ResponsibilitiesDialog = (props) => {
  const {
    action,
    data,
    open,
    onAdd,
    onEdit,
    onClose
  } = props;

  const values = {
    id: data ? data.Id : null,
    name: data ? data.Nombre : '',
    weight: data ? data.Peso : 0
  };

  const schema = Yup.object({
    name: Yup.string()
      .min(4, i18n.t('FormsGeneralTexts.min4Chars'))
      .max(1000, i18n.t('FormsGeneralTexts.max1000Chars'))
      .required('*'),
    weight: Yup.number()
      .min(1)
      .max(data.MaxWeight)
      .required('*')
  });

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
    >
      <MUI.DialogTitle>
        {action === 'add' ? (
          `${i18n.t('GenericMessages.add')} ${i18n.t('Responsibilities.responsibility').toLowerCase()}`
        ) : (
          `${i18n.t('GenericMessages.edit')} ${i18n.t('Responsibilities.responsibility').toLowerCase()}`
        )}
        <MUI.DialogBoxClose onClick={onClose}>
          <Icons.Close />
        </MUI.DialogBoxClose>
      </MUI.DialogTitle>

      <Formik
        validateOnMount
        initialValues={values}
        initialErrors={values}
        initialTouched={values}
        validationSchema={schema}
        onSubmit={(values, { resetForm, setSubmitting }) => {
          action === 'add' ? onAdd(values, resetForm, setSubmitting) : onEdit(values, resetForm, setSubmitting);
        }}
      >
        {(formikProps) => {
          return (
            <>
              <MUI.DialogContent>
                {/* Nombre */}
                <MUI.Box padding="16px">
                  <MUI.TextField
                    multiline
                    label={i18n.t('GenericMessages.name')}
                    type="text"
                    name="name"
                    value={formikProps.values.name}
                    characterLimit={1000}
                    onBlur={formikProps.handleBlur}
                    onChange={formikProps.handleChange}
                    error={Boolean(formikProps.touched.name && formikProps.errors.name)}
                    helperText={formikProps.touched.name && formikProps.errors.name}
                    disabled={data.MaxWeight <= 0}
                  />
                </MUI.Box>

                <Styled.BoxWeight>
                  <MUI.Box display="flex" alignItems="center" width="85%">
                    <Typography.Text style={{ paddingRight: '16px' }}>{`${i18n.t('GenericMessages.weight')} (%)`}</Typography.Text>
                    <Styled.BoxSlider>
                      <MUI.Slider
                        name="weight"
                        value={formikProps.values.weight}
                        onChange={(event, value) => formikProps.setFieldValue('weight', value)}
                        aria-labelledby="input-slider"
                        min={1}
                        max={data.MaxWeight}
                        disabled={data.MaxWeight <= 0}
                      />
                    </Styled.BoxSlider>
                  </MUI.Box>
                  <MUI.Box display="flex" justifyContent="flex-end" width="15%">
                    <MUI.InputSlider
                      name="weight"
                      value={formikProps.values.weight}
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      error={Boolean(formikProps.touched.weight && formikProps.errors.weight)}
                      disabled={data.MaxWeight <= 0}
                      inputProps={{
                        step: 1,
                        min: 1,
                        max: data.MaxWeight,
                        type: 'number',
                        'aria-labelledby': 'input-slider',
                      }}
                    />
                  </MUI.Box>
                </Styled.BoxWeight>
              </MUI.DialogContent>
              <MUI.DialogActions>
                <Layout.BoxRequired>
                  {`* ${i18n.t('FormsGeneralTexts.required')}`}
                </Layout.BoxRequired>
                <MUI.ButtonText onClick={onClose}>
                  {i18n.t('DialogConfirm.cancel')}
                </MUI.ButtonText>
                <MUI.ButtonCTA
                  type="button"
                  color="primary"
                  disabled={!formikProps.isValid || formikProps.isSubmitting}
                  onClick={formikProps.handleSubmit}
                >
                  {action === 'add' ? (
                    i18n.t('GenericMessages.add')
                  ) : (
                    i18n.t('DialogConfirm.save')
                  )}
                </MUI.ButtonCTA>
              </MUI.DialogActions>
            </>
          );
        }}
      </Formik>
    </MUI.Dialog>
  );
};

ResponsibilitiesDialog.propTypes = {
  // String que indica la acción de modal (Retornar estatus o rechazar)
  action: PropTypes.oneOf(['add', 'edit']),
  // Objeto con información para el modal.
  data: PropTypes.object,
  // Boolean que indica si el modal esta abierto.
  open: PropTypes.bool.isRequired,
  // Función que se ejecuta cuando se añade una responsabilidad responsabilidad.
  onAdd: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se edita una responsabilidad responsabilidad.
  onEdit: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se cierra el modal.
  onClose: PropTypes.func.isRequired,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxWeight = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 16px;
  width: 100%;
`;

Styled.BoxSlider = styled.div`
  display: flex;
  flex: 1;
  padding: 0 16px;
`;

export default ResponsibilitiesDialog;
